import { batch } from 'valtio-reactive';

import { DiagramHelpers } from '@diagram/helpers/DiagramHelpers';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { diagram as diagramState } from '@diagram/state/diagram';

import { getComponentPayloadValidator } from 'models';
import { diff } from 'helpers/diff';
import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';

import { state as currentProjectState } from 'state/current-project';

const DiagramAutofixService = {
    autofix: () => {
        // Don't change the order of these calls.

        batch(() => {
            linkSubdiagrams();

            removeIncompleteComponentInstances();
            removeOrphanedComponentInstances();
            ensurePortSpecificationAndConfiguration();

            removeOrphanedConnections();

            validateComponentInstanceSpecifications();
        });
    },
};

const linkSubdiagrams = () => {
    Object.values(diagramState.componentInstances).forEach((componentInstance) => {
        if (componentInstance.componentType !== 'reference') {
            return;
        }

        const reference = componentInstance.specifications.reference as string;

        if (reference) {
            ComponentInstanceService.fillFromReference(componentInstance, reference);
        }
    });
};

const knownChangedProducts = [
    '64d123f4ee1f97d54804e8b7', // Stability 30C
    '64cb803c8e16bcfbdf2116a1', // Stabiliti 30C3
];

const printify = (object: any) => {
    return JSON.stringify(object, null, 2);
};

const removeIncompleteComponentInstances = () => {
    Object.entries(diagramState.componentInstances).forEach(([id, componentInstance]) => {
        const handle = () => {
            delete diagramState.componentInstances[id];

            Object.values(diagramState.connections).forEach((connection) => {
                if (connection.from.componentInstanceId === id || connection.to.componentInstanceId === id) {
                    delete diagramState.connections[connection.id];
                }
            });

            console.error('Diagram Autofix: removed incomplete component instance', id, printify(componentInstance));
        };

        if (!componentInstance.specifications || !componentInstance.configuration) {
            handle();
        }
    });
};

const removeOrphanedComponentInstances = () => {
    Object.entries(diagramState.componentInstances).forEach(([id, componentInstance]) => {
        const handle = () => {
            delete diagramState.componentInstances[id];

            console.error('Diagram Autofix: removed ghosted component', id, printify(componentInstance));
        };

        if (!componentInstance.id) {
            handle();
        }
    });
};

const removeOrphanedConnections = () => {
    Object.entries(diagramState.connections).forEach(([id, connection]) => {
        const handle = () => {
            delete diagramState.connections[id];
        };

        if (!connection.id) {
            console.error('Diagram Autofix: connection (missing id)', id, printify(connection));

            return handle();
        }

        const { from, to } = connection;

        if (!from || !to) {
            console.error('Diagram Autofix: connection (missing from | to)', id, printify(connection));

            return handle();
        }

        const fromComponentInstance = diagramState.componentInstances[connection.from.componentInstanceId];
        const toComponentInstance = diagramState.componentInstances[connection.to.componentInstanceId];

        if (!fromComponentInstance) {
            console.error('Diagram Autofix: connection (missing from component instance)', id, printify(connection));

            return handle();
        }

        if (!toComponentInstance) {
            console.error('Diagram Autofix: connection (missing to component instance)', id, printify(connection));

            return handle();
        }

        if (
            from.port !== null &&
            (!fromComponentInstance.specifications.electrical.ports[from.port] ||
                !fromComponentInstance.configuration.ports[from.port])
        ) {
            const message = knownChangedProducts.includes(fromComponentInstance?.componentId || '')
                ? `Diagram Autofix: connection (changed linked product: ${fromComponentInstance?.componentId})`
                : 'Diagram Autofix: connection (missing from port specification/configuration)';

            console.error(message, id, printify(connection), printify(fromComponentInstance));

            return handle();
        }

        if (
            to.port !== null &&
            (!toComponentInstance.specifications.electrical.ports[to.port] ||
                !toComponentInstance.configuration.ports[to.port])
        ) {
            const message = knownChangedProducts.includes(toComponentInstance?.componentId || '')
                ? `Diagram Autofix: connection (changed linked product: ${toComponentInstance?.componentId})`
                : 'Diagram Autofix: connection (missing to port specification/configuration)';

            console.error(message, id, printify(connection), printify(fromComponentInstance));

            return handle();
        }
    });
};

const ensurePortSpecificationAndConfiguration = () => {
    Object.values(diagramState.componentInstances).forEach((componentInstance) => {
        const specificationPorts = componentInstance.specifications.electrical.ports;
        const configurationPorts = componentInstance.configuration.ports;

        if (configurationPorts.length > specificationPorts.length) {
            if (componentInstance.componentId) {
                console.error(
                    `Diagram Autofix: removed ghosted component configurations (linked component)`,
                    printify(componentInstance),
                );
            } else {
                console.error(`Diagram Autofix: removed ghosted component configurations`, printify(componentInstance));
            }

            componentInstance.configuration.ports = componentInstance.configuration.ports.slice(
                0,
                specificationPorts.length,
            );
        }

        specificationPorts.forEach((_, index) => {
            const portSpecification = componentInstance.specifications.electrical.ports[index];

            if (!portSpecification) {
                console.error(
                    `Diagram Autofix: missing port specification at index ${index}`,
                    printify(componentInstance),
                );

                componentInstance.specifications.electrical.ports[index] = portSpecification || {};
            }

            const portConfiguration = componentInstance.configuration.ports[index];

            if (!portConfiguration) {
                // When a component is linked it's possible that they added ports at
                // the component level, hence there will be no configuration.
                (componentInstance.componentId ? console.log : console.error)(
                    `Diagram Autofix: missing port configuration at index ${index}`,
                    printify(componentInstance),
                );

                componentInstance.configuration.ports[index] = portConfiguration || {};
            }
        });
    });
};

const validateComponentInstanceSpecifications = () => {
    Object.values(diagramState.componentInstances).forEach((componentInstance) => {
        if (componentInstance.componentId) {
            return;
        }

        const validator = getComponentPayloadValidator(componentInstance.componentType);

        const parsed = validator
            // @ts-ignore pick exists
            .pick({
                electrical: true,
            })
            .safeParse(componentInstance.specifications);

        if (parsed.success) {
            const before = componentInstance.specifications.electrical;
            const after = parsed.data.electrical;

            const { hasUpdates, hasDeletes } = diff(before, after);

            if (hasUpdates || hasDeletes) {
                SyncEngineHelpers.set(componentInstance, 'specifications.electrical', parsed.data.electrical);
            }
        }
    });
};

export { DiagramAutofixService };
